import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Plus, QrCode, Copy, Loader2, MapPin, Check } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { formatMoney } from "@/lib/money";

type Hunt = {
  id: string;
  title: string;
  clue: string | null;
  location_label: string | null;
  qr_code: string;
  reward_points: number;
  reward_amount_cents: number;
  scan_limit: number | null;
  scan_count: number;
  status: string;
  created_at: string;
};

const huntUrl = (code: string) => `${window.location.origin}/hunt/${code}`;
const qrImage = (code: string) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=12&data=${encodeURIComponent(huntUrl(code))}`;

const CreatorHunts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hunts, setHunts] = useState<Hunt[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [active, setActive] = useState<Hunt | null>(null);

  // form state
  const [title, setTitle] = useState("");
  const [clue, setClue] = useState("");
  const [location, setLocation] = useState("");
  const [points, setPoints] = useState("10");
  const [cashCents, setCashCents] = useState("0");
  const [scanLimit, setScanLimit] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("qr_hunts")
      .select("*")
      .eq("creator_id", user.id)
      .order("created_at", { ascending: false });
    setHunts((data ?? []) as Hunt[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [user]);

  const create = async () => {
    if (!user || !title.trim()) return;
    setSaving(true);
    const code = crypto.randomUUID().replace(/-/g, "").slice(0, 12);
    const { data, error } = await supabase
      .from("qr_hunts")
      .insert({
        creator_id: user.id,
        title: title.trim(),
        clue: clue.trim() || null,
        location_label: location.trim() || null,
        qr_code: code,
        reward_points: parseInt(points) || 0,
        reward_amount_cents: parseInt(cashCents) || 0,
        scan_limit: scanLimit ? parseInt(scanLimit) : null,
      })
      .select()
      .single();
    setSaving(false);
    if (error) {
      toast({ title: "Couldn't create hunt", description: error.message, variant: "destructive" });
      return;
    }
    setHunts((p) => [data as Hunt, ...p]);
    setShowForm(false);
    setActive(data as Hunt);
    setTitle("");
    setClue("");
    setLocation("");
    setPoints("10");
    setCashCents("0");
    setScanLimit("");
  };

  const toggleStatus = async (h: Hunt) => {
    const next = h.status === "active" ? "paused" : "active";
    await supabase.from("qr_hunts").update({ status: next }).eq("id", h.id);
    setHunts((p) => p.map((x) => (x.id === h.id ? { ...x, status: next } : x)));
  };

  const copyLink = (code: string) => {
    navigator.clipboard.writeText(huntUrl(code));
    toast({ title: "Link copied" });
  };

  return (
    <MobileShell>
      <header className="bg-gradient-hero text-primary-foreground px-5 pt-8 pb-6">
        <button
          onClick={() => navigate(-1)}
          aria-label="Back"
          className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-white/10 transition-smooth"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="mt-2 flex items-end justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold">QR Hunts</h1>
            <p className="text-sm opacity-90">Hide rewards. Pull fans into the real world.</p>
          </div>
          <Button onClick={() => setShowForm((s) => !s)} variant="gold" size="sm">
            <Plus className="h-4 w-4 mr-1" /> New
          </Button>
        </div>
      </header>

      <div className="px-5 py-5 flex-1 space-y-5">
        {showForm && (
          <div className="rounded-2xl bg-background shadow-card p-4 space-y-3 animate-fade-in">
            <p className="font-semibold">Create hunt</p>
            <div>
              <Label>Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Beach drop #1" />
            </div>
            <div>
              <Label>Clue (optional)</Label>
              <Textarea value={clue} onChange={(e) => setClue(e.target.value)} rows={2} placeholder="By the lifeguard tower at sunset" />
            </div>
            <div>
              <Label>Location label</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Crane Beach" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Points</Label>
                <Input type="number" value={points} onChange={(e) => setPoints(e.target.value)} />
              </div>
              <div>
                <Label>Cash ¢</Label>
                <Input type="number" value={cashCents} onChange={(e) => setCashCents(e.target.value)} />
              </div>
              <div>
                <Label>Limit</Label>
                <Input type="number" value={scanLimit} onChange={(e) => setScanLimit(e.target.value)} placeholder="∞" />
              </div>
            </div>
            <Button onClick={create} disabled={saving || !title.trim()} className="w-full">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate QR"}
            </Button>
          </div>
        )}

        {loading ? (
          <div className="grid place-items-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : hunts.length === 0 ? (
          <div className="rounded-2xl bg-muted/40 p-6 text-center text-sm text-muted-foreground">
            No hunts yet. Tap <span className="font-semibold text-foreground">New</span> to drop one.
          </div>
        ) : (
          <ul className="space-y-3">
            {hunts.map((h) => {
              const full = h.scan_limit && h.scan_count >= h.scan_limit;
              return (
                <li key={h.id} className="rounded-2xl bg-background shadow-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{h.title}</p>
                      {h.location_label && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                          <MapPin className="h-3 w-3" /> {h.location_label}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2 text-xs">
                        <span className="px-2 py-0.5 rounded-full bg-muted">
                          {h.reward_points}pts
                        </span>
                        {h.reward_amount_cents > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-muted">
                            {formatMoney(h.reward_amount_cents)}
                          </span>
                        )}
                        <span className="text-muted-foreground">
                          {h.scan_count}{h.scan_limit ? `/${h.scan_limit}` : ""} scans
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span
                        className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${
                          full ? "bg-muted text-muted-foreground" :
                          h.status === "active" ? "bg-emerald-500/15 text-emerald-600" :
                          "bg-muted text-muted-foreground"
                        }`}
                      >
                        {full ? "Full" : h.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => setActive(h)} className="flex-1">
                      <QrCode className="h-4 w-4 mr-1" /> Show QR
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => copyLink(h.qr_code)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleStatus(h)}>
                      {h.status === "active" ? "Pause" : "Resume"}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {active && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-5 animate-fade-in"
          onClick={() => setActive(null)}
        >
          <div
            className="bg-background rounded-3xl p-5 max-w-sm w-full shadow-elegant animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-display text-lg font-bold text-center">{active.title}</p>
            {active.clue && (
              <p className="text-xs text-muted-foreground text-center mt-1 italic">"{active.clue}"</p>
            )}
            <div className="my-4 grid place-items-center">
              <img
                src={qrImage(active.qr_code)}
                alt="QR code"
                className="rounded-2xl bg-white p-2 w-64 h-64"
              />
            </div>
            <p className="text-xs text-center text-muted-foreground break-all">
              {huntUrl(active.qr_code)}
            </p>
            <div className="flex gap-2 mt-4">
              <Button onClick={() => copyLink(active.qr_code)} variant="outline" className="flex-1">
                <Copy className="h-4 w-4 mr-1" /> Copy link
              </Button>
              <Button onClick={() => setActive(null)} className="flex-1">
                <Check className="h-4 w-4 mr-1" /> Done
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="safe-bottom" />
    </MobileShell>
  );
};

export default CreatorHunts;
